import React from 'react';
import { Link } from 'react-router-dom';
import { Zap, Phone, ShieldCheck, Clock, ArrowRight, Layers, MapPin, Cpu, Star, CheckCircle2, Calendar } from 'lucide-react';
import { companyInfo, portfolioImages, guarantees, blogPosts, testimonials } from '../mockData';
import { Reveal, Eyebrow, SectionHeader, BtnPrimary, BtnGlass, BtnOutline, TechCard, IconBox, Stat } from '../components/ui-kit';
import References from '../components/References';

const Hero = () => (
  <section className="relative min-h-[100svh] flex items-center overflow-hidden bg-abyss text-white noise" data-testid="home-hero">
    <div className="absolute inset-0 circuit-grid" />
    <div
      className="absolute inset-y-0 right-0 w-full lg:w-[62%] bg-cover bg-center mask-fade-left"
      style={{ backgroundImage: 'url(/assets/tse-ouest-team-hero.png)' }}
    />
    <div className="absolute inset-0 bg-gradient-to-r from-abyss via-abyss/85 to-abyss/20" />
    <div className="absolute inset-0 bg-gradient-to-t from-abyss via-transparent to-abyss/40" />
    <div className="glow-orb w-[520px] h-[520px] bg-energy/25 -bottom-56 -left-32" />
    <div className="glow-orb w-[360px] h-[360px] bg-signal/15 top-10 right-10" />

    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-36 pb-24 grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-7">
        <Reveal>
          <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-energy/30 bg-energy/10 text-amber font-mono text-xs uppercase tracking-[0.2em] font-semibold shadow-[0_0_15px_rgba(255,94,20,0.2)]">
            <Zap size={14} className="fill-current" /> Électricité & Plâtrerie · Angers
          </span>
        </Reveal>
        <Reveal delay={100}>
          <h1 className="mt-7 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.02]" data-testid="hero-title">
            Votre projet électrique
            <br />
            et de plâtrerie est{' '}
            <span className="text-energy-gradient">unique.</span>
          </h1>
        </Reveal>
        <Reveal delay={200}>
          <p className="mt-7 text-base md:text-lg text-slate-300 max-w-xl leading-relaxed">
            Installations haute précision, mise aux normes NF C 15-100, cloisons et finitions
            architecturales. Une équipe de {companyInfo.experience} ans d'expérience, à Angers et dans tout le 49.
          </p>
        </Reveal>
        <Reveal delay={300} className="mt-10 flex flex-col sm:flex-row gap-4">
          <BtnPrimary to="/contact" data-testid="hero-devis-btn" className="text-base px-8 py-4">
            <Phone size={18} /> Devis gratuit
          </BtnPrimary>
          <BtnGlass to="/services" data-testid="hero-services-btn" className="text-base px-8 py-4">
            Découvrir nos services <ArrowRight size={18} />
          </BtnGlass>
        </Reveal>
        <Reveal delay={400} className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-slate-400">
          <span className="inline-flex items-center gap-2"><ShieldCheck size={16} className="text-signal" /> Conforme NF C 15-100</span>
          <span className="inline-flex items-center gap-2"><Clock size={16} className="text-signal" /> Dépannage 7j/7</span>
          <span className="inline-flex items-center gap-2"><MapPin size={16} className="text-signal" /> Angers · Maine-et-Loire</span>
        </Reveal>
      </div>

      <div className="hidden lg:block lg:col-span-5 relative h-[460px]">
        <Reveal delay={350} className="absolute right-0 top-4 w-72 glass-dark rounded-2xl p-6 float-slow brackets always signal">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-signal">Expérience</p>
          <p className="font-display text-5xl font-black mt-2">{companyInfo.experience}<span className="text-energy">+</span></p>
          <p className="text-sm text-slate-300 mt-1">années de savoir-faire</p>
        </Reveal>
        <Reveal delay={500} className="absolute left-0 bottom-20 w-64 glass-dark rounded-2xl p-6 float-slower">
          <div className="flex items-center gap-3">
            <span className="live-dot w-2.5 h-2.5 rounded-full bg-energy" />
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber">Intervention</p>
          </div>
          <p className="font-display text-3xl font-black mt-3">7j/7</p>
          <p className="text-sm text-slate-300 mt-1">urgences électriques</p>
        </Reveal>
        <Reveal delay={650} className="absolute right-10 bottom-0 w-56 glass-dark rounded-2xl p-5">
          <div className="flex items-center gap-3">
            <IconBox icon={Cpu} tone="signal" className="w-10 h-10" />
            <div>
              <p className="font-semibold text-sm">Domotique</p>
              <p className="text-xs text-slate-400">& éclairage LED</p>
            </div>
          </div>
        </Reveal>
      </div>
    </div>

    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden sm:flex flex-col items-center gap-2 text-slate-500">
      <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Scroll</span>
      <span className="w-px h-10 bg-gradient-to-b from-energy to-transparent" />
    </div>
  </section>
);

const Metrics = () => (
  <section className="bg-[#0b1324] border-y border-white/10 text-white" data-testid="home-metrics">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-2 lg:grid-cols-4 gap-10">
      <Stat value={companyInfo.experience} suffix="+" label="Ans d'expérience" />
      <Stat value={portfolioImages.length} suffix="+" label="Réalisations" />
      <Stat value={100} suffix="%" label="Normes respectées" />
      <Stat value={24} suffix="/7" label="Dépannage urgence" />
    </div>
  </section>
);

const AboutTeaser = () => (
  <section className="relative bg-[#F8FAFC] text-ink py-24 lg:py-32 overflow-hidden" data-testid="home-about">
    <div className="absolute inset-0 circuit-grid-light" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-14 items-center">
      <Reveal className="lg:col-span-5 relative">
        <div className="brackets always rounded-3xl overflow-hidden shadow-[0_30px_60px_rgba(11,17,32,0.15)]">
          <img src="/assets/maison-moderne-hero.png" alt="Réalisation TSE OUEST" className="w-full h-[460px] object-cover" />
        </div>
        <div className="absolute -bottom-6 -right-4 sm:right-8 bg-abyss text-white rounded-2xl p-5 shadow-2xl border border-white/10">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber">Depuis</p>
          <p className="font-display text-3xl font-black">{companyInfo.foundedYear}</p>
          <p className="text-xs text-slate-400">à {companyInfo.city}</p>
        </div>
      </Reveal>
      <div className="lg:col-span-7 lg:pl-6">
        <SectionHeader
          eyebrow="Qui sommes-nous"
          title={<>L'expertise d'un artisan, <span className="text-energy">la précision</span> d'un ingénieur.</>}
          subtitle={`Créée à ${companyInfo.city}, TSE OUEST vous accompagne dans tous vos projets d'électricité générale et de plâtrerie. Une expertise reconnue de plus de ${companyInfo.experience} ans, 100 % adaptée à vos besoins, fidèle à vos idées comme à vos exigences.`}
        />
        <Reveal delay={150} className="mt-8 text-lg font-display font-bold text-ink">
          Imaginez votre projet : <span className="text-energy">nous le réalisons.</span>
        </Reveal>
        <Reveal delay={250} className="mt-10 grid sm:grid-cols-2 gap-4">
          {[
            { to: '/about', icon: ShieldCheck, title: 'TSE OUEST', text: 'Notre histoire, nos valeurs et notre équipe.' },
            { to: '/services', icon: Layers, title: 'Nos services', text: 'Électricité générale et plâtrerie & isolation.' }
          ].map((c) => (
            <Link key={c.to} to={c.to} className="group block" data-testid={`home-link-card-${c.title.toLowerCase().replace(/\s/g, '-')}`}>
              <TechCard className="h-full">
                <IconBox icon={c.icon} tone={c.to === '/about' ? 'ink' : 'energy'} />
                <h3 className="mt-5 font-display text-xl font-bold text-ink flex items-center gap-2">
                  {c.title} <ArrowRight size={16} className="text-energy transition-transform group-hover:translate-x-1" />
                </h3>
                <p className="text-sm text-slate-600 mt-2">{c.text}</p>
              </TechCard>
            </Link>
          ))}
        </Reveal>
      </div>
    </div>
  </section>
);

const Bento = () => (
  <section className="relative bg-abyss text-white py-24 lg:py-32 overflow-hidden noise" data-testid="home-bento">
    <div className="absolute inset-0 dot-matrix opacity-30" />
    <div className="glow-orb w-[500px] h-[500px] bg-energy/15 -top-40 right-0" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHeader
        dark
        eyebrow="Savoir-faire"
        title={<>{companyInfo.experience} ans d'expérience dans l'électricité et la plâtrerie</>}
        subtitle={`TSE OUEST vous apporte l'expertise d'une équipe aguerrie dans le Maine-et-Loire (49) et les départements voisins, en réalisant des installations qui répondent à vos attentes.`}
      />
      <div className="mt-14 grid grid-cols-1 md:grid-cols-6 gap-5">
        <Reveal className="md:col-span-4 relative rounded-3xl overflow-hidden min-h-[320px] group brackets always">
          <img src="/assets/commercial2-restaurant1.webp" alt="Chantier commercial" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-abyss via-abyss/50 to-transparent" />
          <div className="relative p-8 h-full flex flex-col justify-end">
            <Eyebrow className="mb-3">Chantiers professionnels</Eyebrow>
            <h3 className="font-display text-2xl sm:text-3xl font-bold">Commerces, restaurants, bureaux</h3>
            <p className="text-slate-300 mt-2 max-w-md text-sm sm:text-base">Plafonds suspendus, éclairage LED sur rail, tableaux électriques complexes.</p>
          </div>
        </Reveal>
        <Reveal delay={100} className="md:col-span-2">
          <TechCard dark className="h-full group scanline relative overflow-hidden">
            <IconBox icon={Zap} />
            <h3 className="mt-6 font-display text-2xl font-bold">Électricité générale</h3>
            <p className="text-slate-400 text-sm mt-3 leading-relaxed">Installation neuve, rénovation, mise aux normes, dépannage 7j/7, domotique.</p>
            <Link to="/services" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-amber hover:text-white transition-colors">
              En savoir plus <ArrowRight size={14} />
            </Link>
          </TechCard>
        </Reveal>
        <Reveal delay={150} className="md:col-span-2">
          <TechCard dark className="h-full group">
            <IconBox icon={Layers} tone="signal" />
            <h3 className="mt-6 font-display text-2xl font-bold">Plâtrerie & isolation</h3>
            <p className="text-slate-400 text-sm mt-3 leading-relaxed">Cloisons sèches, doublage, plafonds, joints et finitions impeccables.</p>
            <Link to="/services" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-signal hover:text-white transition-colors">
              En savoir plus <ArrowRight size={14} />
            </Link>
          </TechCard>
        </Reveal>
        <Reveal delay={200} className="md:col-span-2">
          <TechCard dark className="h-full flex flex-col justify-between">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400">Zone d'intervention</p>
              <h3 className="mt-3 font-display text-3xl font-black">Angers <span className="text-energy">49</span></h3>
            </div>
            <p className="text-slate-400 text-sm mt-4">Maine-et-Loire et départements limitrophes.</p>
          </TechCard>
        </Reveal>
        <Reveal delay={250} className="md:col-span-2">
          <div className="h-full rounded-2xl p-7 bg-gradient-to-br from-energy to-[#E65100] text-white flex flex-col justify-between shadow-[0_20px_50px_rgba(255,94,20,0.35)]">
            <ShieldCheck size={32} />
            <div>
              <h3 className="font-display text-2xl font-bold mt-6">Certifié & conforme</h3>
              <p className="text-white/85 text-sm mt-2">Normes NF C 15-100 et DTU plâtrerie respectées sur chaque chantier.</p>
            </div>
          </div>
        </Reveal>
      </div>
      <Reveal delay={300} className="mt-12">
        <BtnGlass to="/about" data-testid="home-about-btn">Découvrez l'histoire de notre entreprise <ArrowRight size={16} /></BtnGlass>
      </Reveal>
    </div>
  </section>
);

const Guarantees = () => (
  <section className="bg-white text-ink py-24 lg:py-32 border-y border-slate-200" data-testid="home-guarantees">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-14 items-start">
      <div className="lg:col-span-5 lg:sticky lg:top-32">
        <SectionHeader
          eyebrow="Nos garanties"
          title={<>Des engagements <span className="text-energy">concrets</span>, à chaque étape.</>}
          subtitle="Un savoir-faire unique associé à un accompagnement personnalisé, incluant tous les conseils et garanties nécessaires à votre parfaite sérénité."
        />
        <Reveal delay={150} className="mt-10">
          <BtnPrimary to="/services" data-testid="home-guarantees-btn">Nos atouts pour vos travaux</BtnPrimary>
        </Reveal>
      </div>
      <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
        {guarantees.map((g, i) => (
          <Reveal key={g.id} delay={i * 90}>
            <TechCard className="h-full group">
              <div className="flex items-center justify-between">
                <IconBox icon={CheckCircle2} tone={i % 2 ? 'signal' : 'energy'} />
                <span className="font-mono text-xs text-slate-400">0{i + 1}</span>
              </div>
              <h3 className="mt-5 font-display text-xl font-bold">{g.title}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{g.description}</p>
            </TechCard>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const GalleryTeaser = () => {
  const imgs = portfolioImages.slice(0, 7);
  const spans = ['md:col-span-2 md:row-span-2', '', '', '', '', 'md:col-span-2', ''];
  return (
    <section className="relative bg-obsidian text-white py-24 lg:py-32 overflow-hidden" data-testid="home-gallery">
      <div className="absolute inset-0 circuit-grid opacity-50" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <SectionHeader dark eyebrow="Nos réalisations" title="Galerie Photo" subtitle="Découvrez quelques-unes de nos réalisations en électricité et plâtrerie." />
          <Reveal delay={100}>
            <BtnGlass to="/portfolio" data-testid="home-gallery-btn">Voir toute la galerie <ArrowRight size={16} /></BtnGlass>
          </Reveal>
        </div>
        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 auto-rows-[180px] md:auto-rows-[220px] gap-4">
          {imgs.map((img, i) => (
            <Reveal key={img.id} delay={i * 70} className={spans[i]}>
              <Link to="/portfolio" className="group relative block h-full rounded-2xl overflow-hidden border border-white/10 brackets" data-testid={`home-gallery-item-${img.id}`}>
                <img src={img.url} alt={img.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-abyss/90 via-abyss/20 to-transparent opacity-70 group-hover:opacity-100 transition-opacity" />
                <div className="absolute inset-x-0 bottom-0 p-4 translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber">{img.category}</span>
                  <p className="text-sm font-semibold mt-1 line-clamp-2">{img.title}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Testimonials = () => (
  <section className="bg-[#F1F5F9] text-ink py-24 lg:py-32" data-testid="home-testimonials">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHeader center eyebrow="Avis clients & références" title="Ils nous font confiance" />
      <References />
      <div className="mt-16 grid md:grid-cols-3 gap-6">
        {testimonials.map((t, i) => (
          <Reveal key={t.id} delay={i * 100}>
            <TechCard className="h-full flex flex-col">
              <div className="flex gap-1">
                {[...Array(t.rating)].map((_, k) => <Star key={k} size={16} className="text-energy fill-energy" />)}
              </div>
              <p className="mt-5 text-slate-700 leading-relaxed flex-1">"{t.comment}"</p>
              <div className="mt-6 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-abyss text-white font-display font-bold flex items-center justify-center">
                  {t.name.charAt(0)}
                </div>
                <div>
                  <p className="font-semibold text-sm">{t.name}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-signal">Client vérifié</p>
                </div>
              </div>
            </TechCard>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const News = () => (
  <section className="bg-white text-ink py-24 lg:py-32" data-testid="home-news">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
        <SectionHeader eyebrow="En savoir plus" title="Actualités & innovations" />
        <Reveal delay={100}><BtnOutline to="/blog" data-testid="home-blog-btn">Voir les archives <ArrowRight size={16} /></BtnOutline></Reveal>
      </div>
      <div className="mt-14 grid md:grid-cols-3 gap-6">
        {blogPosts.map((p, i) => (
          <Reveal key={p.id} delay={i * 100}>
            <Link to="/blog" className="group block h-full" data-testid={`home-blog-card-${p.id}`}>
              <TechCard className="h-full p-0 overflow-hidden">
                <div className="h-52 overflow-hidden">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
                </div>
                <div className="p-7">
                  <p className="font-mono text-xs text-slate-500 inline-flex items-center gap-2"><Calendar size={13} /> {p.date}</p>
                  <h3 className="mt-3 font-display text-xl font-bold group-hover:text-energy transition-colors">{p.title}</h3>
                  <p className="text-sm text-slate-600 mt-3 leading-relaxed">{p.excerpt}</p>
                </div>
              </TechCard>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const CTA = ({ title = 'Contactez notre agence à Angers (49)', text = "Besoin d'un électricien ou d'un plâtrier qualifié ? Demandez votre devis gratuit.", btn = 'Prenez rendez-vous' }) => (
  <section className="relative overflow-hidden bg-gradient-to-r from-energy via-[#FF7A2F] to-[#E65100] text-white py-20" data-testid="cta-section">
    <div className="absolute inset-0 dot-matrix opacity-30" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
      <Reveal>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight max-w-2xl">{title}</h2>
        <p className="text-base md:text-lg text-white/85 mt-4 max-w-xl">{text}</p>
      </Reveal>
      <Reveal delay={150} className="flex flex-col sm:flex-row gap-4 shrink-0">
        <Link to="/contact" data-testid="cta-contact-btn" className="inline-flex items-center justify-center gap-2 bg-white text-ink font-bold px-8 py-4 rounded-xl hover:-translate-y-1 hover:shadow-2xl transition-all duration-300">
          {btn} <ArrowRight size={18} />
        </Link>
        <a href={`tel:${companyInfo.phone}`} data-testid="cta-phone-btn" className="inline-flex items-center justify-center gap-2 border-2 border-white/60 text-white font-bold px-8 py-4 rounded-xl hover:bg-white/10 transition-all duration-300">
          <Phone size={18} /> 06 99 13 45 11
        </a>
      </Reveal>
    </div>
  </section>
);

const Home = () => (
  <div className="min-h-screen" data-testid="home-page">
    <Hero />
    <Metrics />
    <AboutTeaser />
    <Bento />
    <Guarantees />
    <GalleryTeaser />
    <Testimonials />
    <News />
    <CTA />
  </div>
);

export default Home;
